import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, TrendingUp, TrendingDown, Minus, Eye, EyeOff } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';
import StarterpaketKarte from '@/components/StarterpaketKarte';
import { UmsatzWert } from '@/components/UmsatzSperre';

/* Übersicht des Unternehmers auf der Heute-Seite (Betreiber 12.10.2026: „überladen … ich muss direkt die Kennzahlen sehen,
   die anstehenden Probetrainings, was sie gemacht hat, die Neukunden; Übergaben nur zum Anklicken").
   Vier Zeilen: Kennzahlen · Vertrieb (Probetrainings, Neukunden) · Team · Betrieb zugeklappt.
   Nach den sieben Dashboard-Regeln (Betreiber 12.10.2026): Zahlen zuerst, EIN Zeitraum-Umschalter für alle zeitbezogenen
   Kacheln, jede davon mit der Veränderung zum Zeitraum davor, und EIN Verlauf über zwölf Wochen — nicht mehr.
   Alles kommt aus /api/startseite; ein Block, den die Person nicht sehen darf oder der gerade fehlt, bleibt weg. */

const ruhig = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const euro = (n, stellen = 2) => Number(n || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: stellen, minimumFractionDigits: stellen });
const kurz = (iso) => iso ? new Date(`${String(iso).slice(0, 10)}T12:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
const kuerzel = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(t => t[0].toUpperCase()).join('');
const stunden = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')} h`;
const WEG = { meta: 'Meta', google: 'Google', instagram: 'Instagram', whatsapp: 'WhatsApp', whatsapp_community: 'WhatsApp-Gruppe', empfehlung: 'Empfehlung',
  flyer: 'Flyer', aushang: 'Aushang', laufkundschaft: 'Laufkundschaft', direkt: 'direkt' };

function wann(tag, heute) {
  const diff = Math.round((new Date(`${tag}T12:00`) - new Date(`${heute}T12:00`)) / 86400000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  return new Date(`${tag}T12:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

/* Zahl zählt beim Erscheinen hoch — außer bei „Bewegung reduzieren" */
function Zahl({ wert, format = (z) => z }) {
  const ziel = Number(wert) || 0;
  const [z, setZ] = useState(ruhig() ? ziel : 0);
  const lauf = useRef(0);
  useEffect(() => {
    if (ruhig()) { setZ(ziel); return undefined; }
    const t0 = performance.now();
    const schritt = (t) => {
      const p = Math.min(1, (t - t0) / 800);
      setZ(Math.round(ziel * (1 - (1 - p) ** 3)));
      if (p < 1) lauf.current = requestAnimationFrame(schritt);
    };
    lauf.current = requestAnimationFrame(schritt);
    return () => cancelAnimationFrame(lauf.current);
  }, [ziel]);
  return <>{format(z)}</>;
}

/* Veränderung zum Zeitraum davor: Pfeil und Differenz. `invers` = weniger ist besser (Kündigungen). */
function Wandel({ wert, vorher, invers, hell }) {
  if (vorher == null || wert == null) return null;
  const diff = wert - vorher;
  const gut = diff === 0 ? null : (diff > 0) !== !!invers;
  const Icon = diff === 0 ? Minus : diff > 0 ? TrendingUp : TrendingDown;
  const farbe = hell ? '' : gut === null ? 'text-muted-foreground' : gut ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  return <span className={`inline-flex items-center gap-0.5 font-semibold tabular-nums ${farbe}`}><Icon className="w-3 h-3" />{diff > 0 ? '+' : ''}{diff}</span>;
}

function Kennzahl({ titel, wert, format, unter, haupt, ziel, verzug = 0, vorher, invers }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(ziel)} style={{ animationDelay: `${verzug * 60}ms` }}
      className={`p1-rein p1-hebt group min-w-0 rounded-2xl border p-4 text-left shadow-[var(--schatten)] ${haupt
        ? 'border-transparent bg-gradient-to-br from-primary to-marke-dunkel text-primary-foreground'
        : 'border-border/60 bg-card'}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`text-xs font-semibold ${haupt ? 'opacity-90' : 'text-muted-foreground'}`}>{titel}</span>
        <ArrowUpRight className={`w-4 h-4 flex-none transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${haupt ? 'opacity-80' : 'text-muted-foreground'}`} />
      </div>
      <div className="mt-2 text-3xl font-extrabold leading-none tracking-tight tabular-nums"><Zahl wert={wert} format={format} /></div>
      <div className={`mt-2 flex flex-wrap items-center gap-x-2 text-xs ${haupt ? 'opacity-90' : 'text-muted-foreground'}`}><Wandel wert={wert} vorher={vorher} invers={invers} hell={haupt} /><span>{unter}</span></div>
    </button>
  );
}

export function Klappe({ titel, zaehler, offen, onToggle, children }) {
  return (
    <div className="border-t border-border/60 first:border-t-0">
      <button onClick={onToggle} aria-expanded={offen} className="flex w-full items-center gap-3 px-5 py-3 text-left text-sm transition-colors hover:bg-accent/60">
        <ChevronRight className={`w-4 h-4 flex-none text-muted-foreground transition-transform duration-300 ${offen ? 'rotate-90' : ''}`} />
        <span className="font-semibold">{titel}</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">{zaehler}</span>
      </button>
      <div className="p1-klappe" data-offen={offen} inert={!offen}>
        <div><div className="px-5 pb-5 pt-1">{offen && children}</div></div>
      </div>
    </div>
  );
}

export { wann, kuerzel };

export default function StartUebersicht({ vorTeam }) {
  const [d, setD] = useState(null);
  const [auf, setAuf] = useState(null);           // welcher Betriebs-Block aufgeklappt ist
  const [ring, setRing] = useState(0);
  const navigate = useNavigate();
  const [zeitraum, setZeitraum] = useState(() => { try { return localStorage.getItem('p1_start_zeitraum') || 'monat'; } catch { return 'monat'; } });
  // Beträge verbergen (Betreiber 12.10.2026: „mit dem kleinen Auge … damit nicht jeder sieht"): gilt je Gerät, Standard
  // verborgen — wer den Bildschirm sieht, sieht ohne Klick keinen Euro-Betrag.
  const [geldSichtbar, setGeldSichtbar] = useState(() => { try { return localStorage.getItem('p1_start_geld') === '1'; } catch { return false; } });
  const geldSchalten = () => setGeldSichtbar(v => { try { localStorage.setItem('p1_start_geld', v ? '0' : '1'); } catch { /* gilt bis zum Neuladen */ } return !v; });
  const geld = (n, stellen = 2) => (geldSichtbar ? euro(n, stellen) : '•••• €');
  const waehle = (z) => { setZeitraum(z); try { localStorage.setItem('p1_start_zeitraum', z); } catch { /* gilt bis zum Neuladen */ } };
  useEffect(() => { api.get('/startseite', { params: { zeitraum } }).then(r => setD(r.data)).catch(() => setD(false)); }, [zeitraum]);
  const einzug = d ? d.finanzen?.einzug : null;
  const anteil = einzug?.gesamt ? einzug.freigegeben / einzug.gesamt : 0;
  useEffect(() => { if (auf !== 'einzug') { setRing(0); return undefined; } const t = setTimeout(() => setRing(anteil), 150); return () => clearTimeout(t); }, [anteil, auf]);
  if (d === null) return <p className="text-sm text-muted-foreground">Übersicht wird geladen …</p>;
  if (!d) return <p className="text-sm text-muted-foreground">Die Übersicht ist gerade nicht erreichbar. Die Arbeitsliste funktioniert weiter.</p>;

  const { probetrainings: pt, kennzahlen: kz, vertraege: ve, checkins: ci, finanzen: fi, neukunden: nk, team, betrieb } = d;
  const zr = d.zeitraum || { key: 'woche', label: 'Diese Woche', vor_label: 'Vorwoche', auswahl: [] };
  const davor = (n) => `${zr.vor_label}: ${n}`;
  const kacheln = [
    pt && { titel: 'Probetrainings', wert: pt.im_zeitraum ?? pt.woche, vorher: pt.vorher ?? pt.vorwoche, ziel: '/probetrainings', unter: davor(pt.vorher ?? pt.vorwoche) },
    ve && { titel: 'Abschlüsse', wert: ve.im_zeitraum ?? ve.monat, vorher: ve.vorher ?? ve.vormonat, ziel: '/vertraege', unter: davor(ve.vorher ?? ve.vormonat) },
    kz?.kuendigungen && { titel: 'Kündigungen', wert: kz.kuendigungen.wert, vorher: kz.kuendigungen.vorher, invers: true, ziel: '/mitgliedschaften', unter: davor(kz.kuendigungen.vorher) },
    kz?.abschlussquote && { titel: 'Abschlussquote', wert: kz.abschlussquote.wert, format: (z) => `${z} %`, ziel: '/kennzahlen/vertrieb', unter: kz.abschlussquote.zusatz || 'laufender Monat' },
    kz?.bestand && { titel: 'Mitglieder aktiv', wert: kz.bestand.wert, ziel: '/mitgliedschaften', unter: kz.bestand.ziel ? `Stand heute · Ziel ${kz.bestand.ziel}` : 'Stand heute' },
    kz?.umsatz_monat && { titel: 'Beiträge je Monat', wert: kz.umsatz_monat.wert, format: (z) => geld(z, 0), ziel: '/cashflow',
      unter: kz.umsatz_monat.ziel ? `Stand heute · Ziel ${geld(kz.umsatz_monat.ziel, 0)}` : 'Stand heute' },
    fi && { titel: 'Rechnungen zum Freigeben', wert: fi.zum_freigeben, ziel: '/rechnungen', unter: `nächster Einzug am ${kurz(fi.einzug.einzugstag)}` },
    // E13 (15.10.2026): Ernährungs-Abos — Stand heute, Quote an den aktiven Mitgliedern, neue diese Woche
    // v9 A (23.10.2026): wer trainiert mindestens einmal je Woche (Ø der letzten 4 Wochen), pausierte zählen nicht
    d.teilnahme && d.teilnahme.basis > 0 && { titel: 'Trainiert jede Woche', wert: d.teilnahme.anteil, format: (z) => `${z} %`, ziel: '/teilnahme',
      unter: `${d.teilnahme.anzahl} von ${d.teilnahme.basis} Mitgliedern · Ø 4 Wochen` },
    d.ernaehrung && { titel: 'Ernährungs-Abos', wert: d.ernaehrung.abos_aktiv, ziel: '/ernaehrung', unter: `Quote ${d.ernaehrung.quote_prozent} % · neu diese Woche ${d.ernaehrung.neue_woche}${d.ernaehrung.anfragen_offen ? ` · ${d.ernaehrung.anfragen_offen} Anfrage${d.ernaehrung.anfragen_offen === 1 ? '' : 'n'} offen` : ''}` },
  ].filter(Boolean);
  const verlauf = (d.verlauf || []).map(v => ({ ...v, name: `KW ${v.kw}` }));
  const akzent = (() => { try { return `hsl(${getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()})`; } catch { return '#E60000'; } })();
  const leise = (() => { try { return `hsl(${getComputedStyle(document.documentElement).getPropertyValue('--muted-foreground').trim()})`; } catch { return '#6B7280'; } })();

  const max = ci ? Math.max(1, ...ci.tage.map(t => t.zahl)) : 1;
  const heuteCheckins = ci?.tage.find(t => t.heute)?.zahl ?? 0;
  const umfang = 2 * Math.PI * 52;
  const klappe = (name) => ({ offen: auf === name, onToggle: () => setAuf(auf === name ? null : name) });

  return (
    <section className="space-y-4" data-testid="start-uebersicht" aria-label="Übersicht">
      {/* Zeitraum: ein Umschalter für alle zeitbezogenen Zahlen */}
      {zr.auswahl?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="start-zeitraum">
          <span className="text-xs font-semibold text-muted-foreground">Zeitraum</span>
          <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs font-semibold" role="group" aria-label="Zeitraum">
            {zr.auswahl.map(z => (
              <button key={z.key} onClick={() => waehle(z.key)} aria-pressed={zr.key === z.key}
                className={`rounded-full px-3 py-1.5 transition-colors ${zr.key === z.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{z.label}</button>
            ))}
          </div>
          <button type="button" onClick={geldSchalten} aria-pressed={geldSichtbar} data-testid="start-geld-auge"
            title={geldSichtbar ? 'Beträge verbergen' : 'Beträge anzeigen'}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground">
            {geldSichtbar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}{geldSichtbar ? 'Beträge verbergen' : 'Beträge anzeigen'}
          </button>
        </div>
      )}

      {/* Umsatz-Sperre (30.10.2026): Beiträge je Monat nur nach Passwort; nur der Inhaber sieht die Zeile */}
      {d.ist_inhaber && d.umsatz_gesperrt && (
        <div className="flex items-center gap-2 text-sm" data-testid="start-umsatz-gesperrt">
          <span className="font-semibold">Beiträge je Monat</span><UmsatzWert wert={null} />
        </div>
      )}

      {/* 1 · Kennzahlen */}
      {kacheln.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7" data-testid="start-kennzahlen">
          {kacheln.map((k, i) => <Kennzahl key={k.titel} {...k} haupt={i === 0} verzug={i} />)}
        </div>
      )}

      {/* Verlauf: EIN Diagramm, zwölf Wochen */}
      {verlauf.length > 0 && (
        <Card data-testid="start-verlauf">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-bold">Entwicklung der letzten zwölf Wochen</h2>
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" />Abschlüsse</span>
                {verlauf[0].probetrainings != null && <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-muted-foreground" />Probetrainings</span>}
              </div>
            </div>
            <div className="mt-3 h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={verlauf} margin={{ top: 6, right: 8, bottom: 0, left: -22 }}>
                  <CartesianGrid vertical={false} stroke={leise} strokeOpacity={0.15} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(w, n) => [w, n === 'abschluesse' ? 'Abschlüsse' : 'Probetrainings']} labelFormatter={(l, p) => `${l}${p?.[0]?.payload?.laufend ? ' (läuft)' : ''}`}
                    contentStyle={{ borderRadius: 12, fontSize: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))' }} />
                  {verlauf[0].probetrainings != null && <Line type="monotone" dataKey="probetrainings" stroke={leise} strokeWidth={2} dot={false} strokeDasharray="4 4" />}
                  <Line type="monotone" dataKey="abschluesse" stroke={akzent} strokeWidth={2.5} dot={{ r: 3, fill: akzent }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2 · Vertrieb: wer kommt, wer ist neu */}
      {(pt || nk) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {pt && (
            <Card data-testid="start-probetrainings">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-bold">Anstehende Probetrainings</h2>
                    <p className="text-xs text-muted-foreground mb-2">{pt.kommend === 1 ? 'Ein Termin steht an.' : `${pt.kommend} Termine stehen an.`}{pt.ohne_termin ? ` ${pt.ohne_termin} ohne Termin.` : ''}</p>
                  </div>
                  <button className="text-xs font-medium text-primary hover:underline" onClick={() => navigate('/probetrainings')}>Alle ansehen</button>
                </div>
                {!pt.naechste.length && <p className="text-sm text-muted-foreground">Zurzeit ist kein Probetraining gebucht.</p>}
                <ul>
                  {pt.naechste.map(p => (
                    <li key={p.lead_id}>
                      <button onClick={() => navigate(`/leads/${p.lead_id}`)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-accent">
                        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{kuerzel(p.name)}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">{p.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">{wann(p.tag, d.heute)}{p.uhrzeit ? ` ${p.uhrzeit}` : ''}{p.kurs ? ` · ${p.kurs}` : ''}</span>
                        </span>
                        <span className={`flex-none rounded-full border px-2 py-0.5 text-[11px] font-semibold ${p.mitglied ? 'border-border text-muted-foreground' : p.im_kurs_gebucht ? 'border-emerald-600/50 text-emerald-700 dark:text-emerald-400' : 'border-amber-600/50 text-amber-700 dark:text-amber-400'}`}>
                          {p.mitglied ? 'Mitglied' : p.im_kurs_gebucht ? 'Platz gebucht' : 'ohne Kursplatz'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {nk && (
            <Card data-testid="start-neukunden">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-bold">Neukunden · {zr.label}</h2>
                    <p className="text-xs text-muted-foreground mb-2">{nk.anzahl === 1 ? 'Ein Abschluss' : `${nk.anzahl} Abschlüsse`}{nk.beitrag_summe != null && `, zusammen ${geld(nk.beitrag_summe)} Beitrag im Monat`}.</p>
                  </div>
                  <button className="text-xs font-medium text-primary hover:underline" onClick={() => navigate('/vertraege')}>Alle Verträge</button>
                </div>
                {!nk.liste.length && <p className="text-sm text-muted-foreground">In diesem Zeitraum gab es keinen Abschluss.</p>}
                <ul className="max-h-64 overflow-y-auto">
                  {nk.liste.map((n, i) => (
                    <li key={`${n.lead_id}-${i}`}>
                      <button onClick={() => n.lead_id && navigate(`/leads/${n.lead_id}`)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-accent">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">{n.name || 'ohne Namen'}</span>
                          <span className="block truncate text-xs text-muted-foreground">{[n.tarif, n.herkunft && `über ${WEG[n.herkunft] || n.herkunft}`, n.berater && `durch ${n.berater.split(' ')[0]}`].filter(Boolean).join(' · ')}</span>
                        </span>
                        <span className="flex-none text-right">
                          <span className="block text-sm font-bold tabular-nums">{n.vorauszahler ? 'Vorauszahler' : geld(n.beitrag)}</span>
                          <span className="block text-[11px] text-muted-foreground tabular-nums">{kurz(n.am)}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Übergabe-Feld der Leitung: bei den Menschen, nicht über den Zahlen */}
      {vorTeam}

      {/* 3 · Team: was wurde gemacht */}
      {team && (
        <Card data-testid="start-team">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold">Team</h2>
                <p className="text-xs text-muted-foreground mb-2">Was heute und in den letzten sieben Tagen erledigt wurde.</p>
              </div>
              <button className="text-xs font-medium text-primary hover:underline" onClick={() => navigate('/arbeitsbericht')}>Arbeitsbericht</button>
            </div>
            {!team.length ? <p className="text-sm text-muted-foreground">In den letzten sieben Tagen hat niemand im Team etwas erfasst.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground"><tr>
                    <th className="py-1.5 text-left font-semibold">Wer</th>
                    <th className="py-1.5 text-right font-semibold">Heute</th><th className="py-1.5 text-right font-semibold">7 Tage</th>
                    <th className="py-1.5 text-right font-semibold">Aufgaben</th><th className="py-1.5 text-right font-semibold">Anrufe</th>
                    <th className="py-1.5 text-right font-semibold">Abschlüsse</th><th className="py-1.5 text-right font-semibold">Stunden</th>
                    <th className="py-1.5 text-right font-semibold">Überfällig</th>
                  </tr></thead>
                  <tbody>
                    {team.map(t => {
                      const ueber = t.ueberfaellig ?? 0;
                      return (
                        <tr key={t.user_id} className="border-t border-border/50">
                          <td className="py-2 whitespace-nowrap font-semibold">{t.name}{t.laeuft && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">arbeitet gerade</span>}</td>
                          <td className="py-2 text-right tabular-nums font-bold">{t.heute.gesamt}</td>
                          <td className="py-2 text-right tabular-nums">{t.woche.gesamt}</td>
                          <td className="py-2 text-right tabular-nums">{t.woche.aufgaben_erledigt + t.woche.follow_ups_erledigt}</td>
                          <td className="py-2 text-right tabular-nums">{t.woche.anrufe}</td>
                          <td className="py-2 text-right tabular-nums">{t.woche.vertraege}</td>
                          <td className="py-2 text-right tabular-nums whitespace-nowrap">{stunden(t.stunden_woche_min)}</td>
                          <td className={`py-2 text-right tabular-nums ${ueber ? 'font-bold text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{ueber}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-2 text-[11px] text-muted-foreground">„Heute“ und „7 Tage“ zählen alles Erfasste: Aufgaben, Anrufe, Nachrichten, Notizen, Abschlüsse. Die Spalten daneben gelten für sieben Tage.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 4 · Betrieb: zugeklappt, ein Klick öffnet */}
      {(betrieb || fi || ci) && (
        <Card data-testid="start-betrieb">
          <CardContent className="p-0">
            {betrieb && (
              <Klappe titel="Starterpakete" zaehler={betrieb.starterpakete_offen ? `${betrieb.starterpakete_offen} offen` : 'alle ausgegeben'} {...klappe('starter')}>
                <StarterpaketKarte />
              </Klappe>
            )}
            {fi && (
              <Klappe titel={`Einzug am ${kurz(fi.einzug.einzugstag)}`} zaehler={`${Math.round(anteil * 100)} % freigegeben`} {...klappe('einzug')}>
                <div className="flex flex-wrap items-center gap-5">
                  <svg viewBox="0 0 132 132" className="h-28 w-28 flex-none" role="img" aria-label={`${Math.round(anteil * 100)} Prozent der Rechnungen freigegeben`}>
                    <circle cx="66" cy="66" r="52" fill="none" strokeWidth="13" style={{ stroke: 'hsl(var(--muted))' }} />
                    <circle cx="66" cy="66" r="52" fill="none" strokeWidth="13" strokeLinecap="round" transform="rotate(-90 66 66)"
                      style={{ stroke: 'hsl(var(--primary))', strokeDasharray: umfang, strokeDashoffset: umfang * (1 - (ruhig() ? anteil : ring)), transition: ruhig() ? 'none' : 'stroke-dashoffset 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)' }} />
                    <text x="66" y="75" textAnchor="middle" style={{ fill: 'hsl(var(--foreground))', font: '800 26px Manrope, sans-serif' }}>{Math.round(anteil * 100)} %</text>
                  </svg>
                  <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-sm">
                    <dt className="text-muted-foreground">Rechnungen freigegeben</dt><dd className="text-right font-bold tabular-nums">{fi.einzug.freigegeben} von {fi.einzug.gesamt}</dd>
                    <dt className="text-muted-foreground">Freigegeben, noch nicht eingereicht</dt><dd className="text-right font-bold tabular-nums"><UmsatzWert wert={fi.einzug.summe_offen} format={geld} kurz /></dd>
                    <dt className="text-muted-foreground">Abgabe bei der Bank bis</dt><dd className="text-right font-bold tabular-nums">{kurz(fi.einzug.abgabe_bis)}</dd>
                  </dl>
                  <button className="text-xs font-medium text-primary hover:underline" onClick={() => navigate('/rechnungen')}>Zum Rechnungslauf</button>
                </div>
              </Klappe>
            )}
            {ci && (
              <Klappe titel="Check-ins" zaehler={`heute ${heuteCheckins} · diese Woche ${ci.summe}`} {...klappe('checkins')}>
                <p className="text-xs text-muted-foreground mb-3">Besuche je Tag. Helle Balken sind der Schnitt der letzten vier Wochen.</p>
                <div className="grid grid-cols-7 items-end gap-2 sm:gap-3 h-36" role="img"
                  aria-label={`Check-ins je Wochentag: ${ci.tage.map(t => `${t.tag} ${t.kommt ? 'erwartet etwa ' : ''}${t.zahl}`).join(', ')}`}>
                  {ci.tage.map((t, n) => (
                    <div key={t.datum} className="flex h-full flex-col items-center justify-end gap-1.5 text-[11px] text-muted-foreground" title={`${t.tag} ${kurz(t.datum)}: ${t.kommt ? 'erwartet etwa ' : ''}${t.zahl}`}>
                      <span className={`tabular-nums font-bold ${t.kommt ? 'text-muted-foreground' : 'text-foreground'}`}>{t.kommt ? `~${t.zahl}` : t.zahl}</span>
                      <div className={`p1-balken w-full max-w-[44px] rounded-full bg-primary ${t.heute ? '' : t.kommt ? 'opacity-20' : 'opacity-45'}`}
                        style={{ height: `${Math.max(4, (t.zahl / max) * 78)}%`, animationDelay: `${n * 70}ms` }} />
                      <span className={t.heute ? 'font-bold text-foreground' : ''}>{t.tag}</span>
                    </div>
                  ))}
                </div>
              </Klappe>
            )}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
